import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Camera, Loader, Save, ChefHat, MapPin } from 'lucide-react';
import { UPDATE_PROFILE_URL, UPDATE_CURRENT_LOCATION_URL } from '../../config/api';
import { getCurrentLocationDetails, getLocationPermissionState, storeCoords } from '../Utils/location';
import './ProfileCompletion.css';

interface ProfileCompletionProps {
  onCompleted: () => void;
}

const ProfileCompletion: React.FC<ProfileCompletionProps> = ({ onCompleted }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken') || '';
  const photoInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    gender: '',
    dob: '',
    city: '',
    state: '',
    pincode: '',
    address: '',
    latitude: '',
    longitude: '',
    bio: '',
    food_preferences: '',
    dietary_preference: '',
    favorite_cuisine: '',
  });
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [locationStatus, setLocationStatus] = useState<'idle' | 'detecting' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');
  const [dobError, setDobError] = useState('');

  // Minimum age of 13 years for sign-up
  const minAgeDate = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 13);
    return d.toISOString().split('T')[0];
  })();

  // Auto-detect location on mount if permission is already granted
  useEffect(() => {
    if (getLocationPermissionState() === 'granted') {
      runLocationDetect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFieldChange = (key: string, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const runLocationDetect = async () => {
    setIsDetectingLocation(true);
    setLocationStatus('detecting');
    try {
      const result = await getCurrentLocationDetails();
      if ('type' in result) {
        setLocationStatus('error');
        return;
      }
      storeCoords(result.latitude, result.longitude);
      if (result.city) {
        localStorage.setItem('activeCity', result.city.trim());
        window.dispatchEvent(new Event('activeCityChanged'));
      }
      setForm(prev => ({
        ...prev,
        city: result.city || prev.city,
        state: result.state || prev.state,
        pincode: result.postal_code || prev.pincode,
        address: result.full_address || prev.address,
        latitude: result.latitude != null ? String(result.latitude) : prev.latitude,
        longitude: result.longitude != null ? String(result.longitude) : prev.longitude,
      }));
      setLocationStatus('success');

      if (result.latitude != null && result.longitude != null) {
        try {
          await fetch(UPDATE_CURRENT_LOCATION_URL(result.latitude, result.longitude, result.full_address), {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, accept: 'application/json' },
          });
        } catch {
          // non-critical
        }
      }
    } catch {
      setLocationStatus('error');
    } finally {
      setIsDetectingLocation(false);
    }
  };

  const handleDetectLocation = () => runLocationDetect();

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const validateDob = (): boolean => {
    if (!form.dob) return true; // optional field
    const dob = new Date(form.dob);
    if (isNaN(dob.getTime())) {
      setDobError('Please enter a valid date.');
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dob > today) {
      setDobError('Date of birth cannot be in the future.');
      return false;
    }
    const minAge = new Date();
    minAge.setFullYear(minAge.getFullYear() - 13);
    minAge.setHours(0, 0, 0, 0);
    if (dob > minAge) {
      setDobError('You must be at least 13 years old to use HomeCookt.');
      return false;
    }
    setDobError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateDob()) return;
    setIsSaving(true);
    setError('');

    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, val]) => {
        const fieldName = key === 'phone' ? 'phone_number' : key;
        if (key === 'latitude' || key === 'longitude') {
          const num = parseFloat(val);
          if (!isNaN(num)) formData.append(fieldName, String(num));
        } else if (val.trim() !== '') {
          formData.append(fieldName, val);
        }
      });
      if (photoFile) formData.append('profile_photo', photoFile);

      const res = await fetch(UPDATE_PROFILE_URL, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const json = await res.json();

      if (res.ok && json.success) {
        if (json.data?.name) localStorage.setItem('userName', json.data.name);
        if (json.data?.city) localStorage.setItem('activeCity', json.data.city.trim());
        localStorage.setItem('profileCompleted', 'true');

        // Sync location to backend if coordinates are set
        const lat = parseFloat(form.latitude);
        const lon = parseFloat(form.longitude);
        if (!isNaN(lat) && !isNaN(lon)) {
          try {
            await fetch(UPDATE_CURRENT_LOCATION_URL(lat, lon, form.address || undefined), {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}`, accept: 'application/json' },
            });
          } catch {
            // non-critical
          }
        }

        onCompleted();
        navigate('/home', { replace: true });
      } else {
        setError(json.message || 'Could not save profile. Please try again.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="pc-page">
      <div className="pc-container">
        <div className="pc-header">
          <div className="pc-logo">
            <ChefHat size={32} />
          </div>
          <h1 className="pc-title">Complete Your Profile</h1>
          <p className="pc-subtitle">
            Tell us a little about yourself so we can personalise your HomeCookt experience.
            All fields are optional — fill in what you'd like.
          </p>
        </div>

        <form className="pc-form" onSubmit={handleSubmit}>
          {/* Photo upload */}
          <div className="pc-photo-section">
            <div
              className="pc-avatar"
              onClick={() => photoInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && photoInputRef.current?.click()}
            >
              {photoPreview ? (
                <img src={photoPreview} alt="Profile preview" />
              ) : (
                <User size={44} className="pc-avatar-icon" />
              )}
              <div className="pc-avatar-overlay">
                <Camera size={20} />
              </div>
            </div>
            <p className="pc-photo-hint">Click to add a profile photo</p>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handlePhotoChange}
            />
          </div>

          <div className="pc-grid">
            <div className="pc-field">
              <label htmlFor="pc-name">Full Name</label>
              <input
                id="pc-name"
                type="text"
                value={form.name}
                onChange={e => handleFieldChange('name', e.target.value)}
                placeholder="Your full name"
              />
            </div>
            <div className="pc-field">
              <label htmlFor="pc-phone">Phone Number</label>
              <input
                id="pc-phone"
                type="tel"
                value={form.phone}
                onChange={e => handleFieldChange('phone', e.target.value)}
                placeholder="+91 98765 43210"
              />
            </div>
            <div className="pc-field">
              <label htmlFor="pc-gender">Gender</label>
              <select
                id="pc-gender"
                value={form.gender}
                onChange={e => handleFieldChange('gender', e.target.value)}
              >
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
                <option value="Prefer not to say">Prefer not to say</option>
              </select>
            </div>
            <div className="pc-field">
              <label htmlFor="pc-dob">Date of Birth</label>
              <input
                id="pc-dob"
                type="date"
                value={form.dob}
                onChange={e => {
                  handleFieldChange('dob', e.target.value);
                  if (dobError) setDobError('');
                }}
                max={minAgeDate}
                className={dobError ? 'error' : ''}
              />
              {dobError && <span className="pc-dob-error">{dobError}</span>}
            </div>

            {/* Location section with detect button */}
            <div className="pc-field pc-field--full pc-location-header">
              <span className="pc-section-label">Location</span>
              <button
                type="button"
                className={`pc-detect-btn${isDetectingLocation ? ' pc-detect-btn--loading' : ''}`}
                onClick={handleDetectLocation}
                disabled={isDetectingLocation}
              >
                {isDetectingLocation ? (
                  <><Loader size={14} className="pc-spinner-sm" /> Detecting...</>
                ) : (
                  <><MapPin size={14} /> Detect My Location</>
                )}
              </button>
            </div>
            {locationStatus === 'detecting' && (
              <div className="pc-field pc-field--full">
                <p className="pc-location-success">Detecting your location automatically...</p>
              </div>
            )}
            {locationStatus === 'success' && (
              <div className="pc-field pc-field--full">
                <p className="pc-location-success">Location detected and filled in below.</p>
              </div>
            )}
            {locationStatus === 'error' && (
              <div className="pc-field pc-field--full">
                <p className="pc-location-error">Could not detect location. Please fill in manually.</p>
              </div>
            )}

            <div className="pc-field">
              <label htmlFor="pc-city">City</label>
              <input
                id="pc-city"
                type="text"
                value={form.city}
                onChange={e => handleFieldChange('city', e.target.value)}
                placeholder="Your city"
              />
            </div>
            <div className="pc-field">
              <label htmlFor="pc-state">State</label>
              <input
                id="pc-state"
                type="text"
                value={form.state}
                onChange={e => handleFieldChange('state', e.target.value)}
                placeholder="Your state"
              />
            </div>
            <div className="pc-field">
              <label htmlFor="pc-pincode">Pincode</label>
              <input
                id="pc-pincode"
                type="text"
                value={form.pincode}
                onChange={e => handleFieldChange('pincode', e.target.value)}
                placeholder="Pincode"
              />
            </div>
            <div className="pc-field pc-field--full">
              <label htmlFor="pc-address">Street Address</label>
              <input
                id="pc-address"
                type="text"
                value={form.address}
                onChange={e => handleFieldChange('address', e.target.value)}
                placeholder="Your street address"
              />
            </div>
            <div className="pc-field" style={{ display: 'none' }}>
              <input type="number" step="any" value={form.latitude} readOnly />
            </div>
            <div className="pc-field" style={{ display: 'none' }}>
              <input type="number" step="any" value={form.longitude} readOnly />
            </div>

            <div className="pc-field pc-field--full">
              <label htmlFor="pc-bio">About You</label>
              <textarea
                id="pc-bio"
                value={form.bio}
                onChange={e => handleFieldChange('bio', e.target.value)}
                placeholder="Tell the community a little about yourself..."
                rows={3}
              />
            </div>

            <div className="pc-section-label">Food Preferences</div>
            <div className="pc-field">
              <label htmlFor="pc-food-pref">Food Preferences</label>
              <input
                id="pc-food-pref"
                type="text"
                value={form.food_preferences}
                onChange={e => handleFieldChange('food_preferences', e.target.value)}
                placeholder="e.g. Organic, Home-cooked"
              />
            </div>
            <div className="pc-field">
              <label htmlFor="pc-dietary">Dietary Preference</label>
              <select
                id="pc-dietary"
                value={form.dietary_preference}
                onChange={e => handleFieldChange('dietary_preference', e.target.value)}
              >
                <option value="">Select dietary preference</option>
                <option value="Vegetarian">Vegetarian</option>
                <option value="Non Vegetarian">Non Vegetarian</option>
                <option value="Non Vegetarian & Vegetarian">Non Vegetarian &amp; Vegetarian</option>
              </select>
            </div>
            <div className="pc-field">
              <label htmlFor="pc-cuisine">Favourite Cuisine</label>
              <input
                id="pc-cuisine"
                type="text"
                value={form.favorite_cuisine}
                onChange={e => handleFieldChange('favorite_cuisine', e.target.value)}
                placeholder="e.g. South Indian, Italian"
              />
            </div>
          </div>

          {error && <div className="pc-error">{error}</div>}

          <button type="submit" className="pc-submit-btn" disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader size={18} className="pc-spinner" />
                Saving...
              </>
            ) : (
              <>
                <Save size={18} />
                Save &amp; Continue
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfileCompletion;
